import { BCDataGrid, findMessage } from '@bimser/components';
import { IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isEqual from 'lodash/isEqual';
import DataGrid2 from '../../..';
import { IControlProperties } from '../../../../../../../entities';
import { DataGridEditType, IDataGridColumn, IDataGridRow } from '../../../entities';
import { makeReq, makeUrl } from '../../customStore/utils';

dayjs.extend(localeData);
dayjs.extend(localizedFormat);

export const getCheckUniqueValueComponent = (bcColumn: IDataGridColumn<IControlProperties>) => (
	<BCDataGrid.Components.AsyncRule
		key="checkUniqueValueRule"
		message={findMessage.get('103133').replace('{0}', bcColumn.name)}
		validationCallback={validationCallback}
		reevaluate={false}
	/>
);

const validationCallback = async e => {
	const dataGrid = e.column.retrieveDataGridInstance() as DataGrid2;
	const bcColumn = dataGrid.__getBCColumn(e.column.name);

	if (isNullOrUndefined(e.value)) {
		return true;
	}

	if (dataGrid.__form.documentId && dataGrid.saveMode == 'self') {
		try {
			const keyExpr = dataGrid.getUniqueDataField();
			let row: IDataGridRow;

			if (!dataGrid.__isNewRowEditing && !isNullOrUndefined(e.data[keyExpr])) {
				row = dataGrid.rows.find(row => row.data[keyExpr] == e.data[keyExpr]);
			}

			const res = await makeReq<any>(makeUrl(dataGrid, 'CheckColumnsIsUnique'), {
				documentId: dataGrid.__form.documentId,
				controlName: dataGrid.name,
				rows: [dataGrid.newRow({ data: e.data, rowId: row?.rowId })]
			});

			const invalids = res.data.invalids.at(0);

			if (invalids) {
				return !invalids.invalidColumns.includes(bcColumn.name);
			}

			return true;
		} catch {
			return false;
		}
	} else if (!dataGrid.dataSource.name && dataGrid.dataSourceType === 'dynamic') {
		const keyExpr = dataGrid.getUniqueDataField();

		let rows = dataGrid.rows;

		if (!dataGrid.__isNewRowEditing) {
			rows = rows.filter(r => r.data[keyExpr] !== e.data[keyExpr]);
		}

		return !rows.some(r => {
			const otherValue = r.data[bcColumn.name];

			if (otherValue && e.value) {
				switch (bcColumn.editType) {
					case 'datePicker': {
						const value = e.value as string;

						return checkDates(otherValue, value, bcColumn.format, 'datePicker');
					}
					case 'dateTimePicker': {
						const value = e.value as string;

						return checkDates(otherValue, value, bcColumn.format, 'dateTimePicker');
					}
					case 'timePicker': {
						const value = e.value as string;

						return checkDates(otherValue, value, bcColumn.format, 'timePicker');
					}
					case 'multiLanguageTextBox': {
						const value = e.value as unknown as IDictionary<string>;

						if (MLHelper.isMLEmpty(value)) {
							return false;
						}

						return isEqual(clearMLValue(otherValue), clearMLValue(value));
					}
					case 'comboBox': {
						if (bcColumn.dbType.type === 'object') {
							const value = e.value;

							if (typeof otherValue === 'object' && otherValue) {
								return otherValue.value === value;
							}

							return otherValue === value;
						}

						if (bcColumn.dbType.type === 'string') {
							const value = e.value as string;

							return otherValue.toString() === value.toString();
						}

						const value = e.value;

						return otherValue === value;
					}
					case 'numberBox': {
						const value = e.value;

						return Number(otherValue) === value;
					}
					default: {
						const value: string | number = e.value;

						return otherValue === value;
					}
				}
			}

			return otherValue === e.value;
		});
	}
};

const checkDates = (a: string, b: string, format: string, editType: DataGridEditType) => {
	const left = formatDate(a, format || defaultFormats[editType]);
	const right = formatDate(b, format || defaultFormats[editType]);

	return left === right;
};

const formatDate = (value: string, format: string) => {
	const date = dayjs(value);

	if (!date.isValid()) {
		return value;
	}

	return date.format(format);
};

const defaultFormats = {
	datePicker: dayjs().localeData().longDateFormat('L'),
	dateTimePicker: `${dayjs().localeData().longDateFormat('L')} ${dayjs()
		.localeData()
		.longDateFormat('LT')}`,
	timePicker: 'HH:mm'
};

const clearMLValue = (ml: IDictionary<string>) => {
	const value = { ...ml };

	Object.keys(value).forEach(key => {
		if (!value[key]) {
			delete value[key];
		}
	});

	return value;
};
